import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, BRAND, fileUrl } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { ArrowRight, Sparkles, Shield, Truck } from "lucide-react";

export default function Home() {
  const [newArrivals, setNewArrivals] = useState([]);
  const [bestSellers, setBestSellers] = useState([]);
  const [doodles, setDoodles] = useState([]);
  const [featured, setFeatured] = useState(null);

  useEffect(() => {
    api.get("/products", { params: { sort: "new", limit: 8 } }).then(r => setNewArrivals(r.data.items));
    api.get("/products", { params: { sort: "best", limit: 6 } }).then(r => setBestSellers(r.data.items));
    api.get("/products", { params: { collection: "the-doodles-official-collection", limit: 6 } }).then(r => setDoodles(r.data.items));
    api.get("/featured/design-of-month").then(r => setFeatured(r.data)).catch(()=>{});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative hb-hero-frame text-white overflow-hidden">
        <div className="absolute inset-0 hb-grain" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 pt-20 pb-28 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-xs tracking-[0.35em] uppercase text-white/70 mb-6">Hampton Blu Creations · Est. Family Owned</div>
            <h1 className="font-display text-6xl sm:text-7xl lg:text-8xl leading-[0.9]">
              WEAR YOUR<br/>
              <span className="hb-chrome-text">STORY.</span>
            </h1>
            <p className="mt-6 text-lg text-white/80 max-w-md font-light leading-relaxed">
              Original custom apparel from Hampton Blu Creations. Every design tells a story — pride, family, sports, faith, and the streets of New York.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/collections/graphics-official-collection" className="hb-btn-primary bg-white text-[color:var(--hb-navy)] border-white hover:bg-[color:var(--hb-royal)] hover:text-white hover:border-[color:var(--hb-royal)]" data-testid="hero-shop-btn">Shop All Designs</Link>
              <Link to="/custom-design" className="hb-btn-secondary text-white border-white hover:bg-white hover:text-[color:var(--hb-navy)]" data-testid="hero-custom-btn">Request Custom Design</Link>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg">
              <div className="flex items-start gap-2"><Sparkles size={18} className="text-[color:var(--hb-royal-2)] mt-1"/><div><div className="text-xs uppercase tracking-widest">Original</div><div className="text-xs text-white/60">Designs</div></div></div>
              <div className="flex items-start gap-2"><Shield size={18} className="text-[color:var(--hb-royal-2)] mt-1"/><div><div className="text-xs uppercase tracking-widest">Premium</div><div className="text-xs text-white/60">Quality</div></div></div>
              <div className="flex items-start gap-2"><Truck size={18} className="text-[color:var(--hb-royal-2)] mt-1"/><div><div className="text-xs uppercase tracking-widest">Free Ship</div><div className="text-xs text-white/60">$75+</div></div></div>
            </div>
          </div>
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-tr from-[color:var(--hb-royal)]/40 to-transparent blur-3xl" />
            <img src={BRAND.hero} alt="Hampton Blu Creations" className="relative w-full aspect-square object-cover shadow-2xl" />
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="border-y border-neutral-200 py-6 overflow-hidden bg-white">
        <div className="hb-marquee font-display text-3xl text-[color:var(--hb-navy)]">
          <div className="flex gap-16 pr-16">
            {["The Right Way","Doodle Crew","One United","Big Blue Pride","Empowering Futures","Built On Family","New York Made","The Right Way","Doodle Crew","One United","Big Blue Pride","Empowering Futures"].map((t,i) => (
              <span key={i} className="whitespace-nowrap">✦ {t}</span>
            ))}
          </div>
        </div>
      </div>

      {/* WEAR THE LEGACY — Featured Brand Banner */}
      <section className="relative bg-[color:var(--hb-navy)] overflow-hidden" data-testid="wear-the-legacy-section">
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/60"/>
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 py-16 grid lg:grid-cols-[1.4fr_1fr] gap-10 items-center">
          <Link to="/collections/graphics-official-collection" className="group block relative overflow-hidden shadow-2xl border-4 border-[#D4A64A]" data-testid="wear-the-legacy-image-link">
            <img
              src="https://customer-assets-eiarnc6j.emergentagent.net/job_hampton-blu-auto/artifacts/yqhu11a6_Untitled%20design.webp"
              alt="Hampton Blu Creations — Wear The Legacy"
              className="w-full h-auto transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-8">
              <div className="hb-btn-primary bg-[#D4A64A] text-[color:var(--hb-navy)] border-[#D4A64A]">Start Designing →</div>
            </div>
          </Link>
          <div className="text-white">
            <div className="inline-block text-[10px] tracking-[0.4em] uppercase text-[#D4A64A] border-t border-b border-[#D4A64A] py-1 px-3 mb-6" data-testid="legacy-eyebrow">Family · Faith · Legacy</div>
            <h2 className="font-display text-5xl sm:text-6xl leading-[0.95]">
              Wear<br/>
              <span className="text-[#D4A64A]">The Legacy</span>
            </h2>
            <p className="mt-6 text-lg text-white/85 font-light leading-relaxed">
              Every design tells our story — Family. Faith. New York. Built for the community, worn with purpose. Tap the crest to start designing your own piece of the legacy.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/collections/graphics-official-collection" className="hb-btn-primary bg-[#D4A64A] text-[color:var(--hb-navy)] border-[#D4A64A] hover:bg-white hover:border-white" data-testid="legacy-shop-btn">
                Shop All Designs
              </Link>
              <Link to="/custom-design" className="hb-btn-secondary text-white border-white hover:bg-white hover:text-[color:var(--hb-navy)]" data-testid="legacy-custom-btn">
                Design Your Own
              </Link>
            </div>
            <div className="mt-8 grid grid-cols-3 gap-4 text-center text-xs uppercase tracking-widest text-white/70">
              <div><div className="text-[#D4A64A] font-bold text-2xl font-display">9</div>Official Collections</div>
              <div><div className="text-[#D4A64A] font-bold text-2xl font-display">11</div>Apparel Styles</div>
              <div><div className="text-[#D4A64A] font-bold text-2xl font-display">∞</div>Custom Designs</div>
            </div>
          </div>
        </div>
      </section>

      {/* NEW DROP — featured 3 freshest products with a bold "NEW DROP" ribbon */}
      {newArrivals.length >= 3 && (
        <section className="relative bg-[color:var(--hb-cream)] overflow-hidden" data-testid="new-drop-section">
          <div className="absolute inset-0 opacity-[0.04]" style={{backgroundImage: "radial-gradient(circle at 20% 20%, #1D4ED8 0%, transparent 50%), radial-gradient(circle at 80% 80%, #D4A64A 0%, transparent 50%)"}}/>
          <div className="relative max-w-7xl mx-auto px-6 sm:px-8 py-16">
            <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
              <div>
                <div className="inline-flex items-center gap-2 bg-[color:var(--hb-royal)] text-white text-[10px] tracking-[0.4em] uppercase px-3 py-1.5 font-bold mb-4">
                  <span className="inline-block w-1.5 h-1.5 bg-white rounded-full animate-pulse"/>
                  Just Dropped
                </div>
                <h2 className="font-display text-5xl sm:text-6xl text-[color:var(--hb-navy)] leading-[0.95]">
                  New Drop
                </h2>
                <p className="mt-3 text-sm text-neutral-600 max-w-md">The 3 freshest designs out the gate. Catch them before they move to the main shelf.</p>
              </div>
              <Link to="/collections/graphics-official-collection" className="text-sm uppercase tracking-widest inline-flex items-center gap-1 hover:text-[color:var(--hb-royal)]" data-testid="new-drop-view-all">
                View all drops <ArrowRight size={16}/>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6" data-testid="new-drop-grid">
              {newArrivals.slice(0, 3).map((p, i) => (
                <div key={p.product_id} className="relative group">
                  <div className="absolute top-3 left-3 z-10 bg-[color:var(--hb-navy)] text-white text-[9px] tracking-[0.3em] uppercase px-2.5 py-1 font-bold shadow-md" data-testid={`new-ribbon-${i}`}>
                    NEW
                  </div>
                  <ProductCard product={p} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* NEW ARRIVALS */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-neutral-500 mb-2">Fresh drops</div>
            <h2 className="font-display text-5xl text-[color:var(--hb-navy)]">New Arrivals</h2>
          </div>
          <Link to="/collections/graphics-official-collection" className="text-sm uppercase tracking-widest inline-flex items-center gap-1 hover:text-[color:var(--hb-royal)]" data-testid="view-all-newarrivals">View all <ArrowRight size={16}/></Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="new-arrivals-grid">
          {newArrivals.map(p => <ProductCard key={p.product_id} product={p} />)}
        </div>
      </section>

      {/* COLLECTION BENTO */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link to="/collections/the-doodles-official-collection" className="relative aspect-[4/5] md:col-span-2 md:row-span-2 md:aspect-auto overflow-hidden bg-[color:var(--hb-warm)] group" data-testid="bento-doodles">
            <img src="https://images.unsplash.com/photo-1547525623-c7d42c20284c?w=1200" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" alt="The Doodles Official Collection" />
            <div className="absolute inset-0 bg-gradient-to-tr from-black/50 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 text-white">
              <div className="text-xs tracking-widest uppercase opacity-80">The Pack</div>
              <div className="font-display text-4xl mt-1 leading-tight">The Doodles<br/>Official Collection</div>
              <div className="mt-3 text-sm inline-flex items-center gap-1 border-b border-white/60">Explore the Collection <ArrowRight size={14}/></div>
            </div>
          </Link>
          <Link to="/collections/one-united-official-collection" className="relative aspect-[4/3] overflow-hidden bg-[color:var(--hb-warm)] group" data-testid="bento-oneunited">
            <div className="absolute inset-0 hb-hero-frame" />
            <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center px-6">
              <div className="text-xs tracking-widest uppercase opacity-80">Movement</div>
              <div className="font-display text-3xl mt-1 leading-tight">One United<br/>Official Collection</div>
              <div className="mt-3 text-xs opacity-80">United in purpose. Built on family.</div>
            </div>
          </Link>
          <Link to="/collections/sports-official-collection" className="relative aspect-[4/3] overflow-hidden bg-[color:var(--hb-warm)] group" data-testid="bento-sports">
            <img src="https://images.unsplash.com/photo-1759476530203-b11cc8c87d43?w=800" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" alt="Sports Official Collection" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute bottom-4 left-6 text-white">
              <div className="text-xs tracking-widest uppercase opacity-80">Ride or Die</div>
              <div className="font-display text-3xl mt-1 leading-tight">Sports<br/>Official Collection</div>
            </div>
          </Link>
        </div>
      </section>

      {/* BEST SELLERS */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-neutral-500 mb-2">Most loved</div>
            <h2 className="font-display text-5xl text-[color:var(--hb-navy)]">Best Sellers</h2>
          </div>
          <Link to="/collections/graphics-official-collection" className="text-sm uppercase tracking-widest inline-flex items-center gap-1 hover:text-[color:var(--hb-royal)]">View all <ArrowRight size={16}/></Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6" data-testid="best-sellers-grid">
          {bestSellers.slice(0,6).map(p => <ProductCard key={p.product_id} product={p} />)}
        </div>
      </section>

      {/* DESIGN OF THE MONTH */}
      {featured?.design && featured?.sample_product && (
        <section className="max-w-7xl mx-auto px-6 sm:px-8 py-16" data-testid="design-of-month">
          <div className="grid lg:grid-cols-2 gap-10 items-center bg-white border border-neutral-200 overflow-hidden">
            <Link to={`/product/${featured.sample_product.product_id}`} className="aspect-square bg-[color:var(--hb-warm)] overflow-hidden group">
              {featured.sample_product.mockups?.[featured.sample_product.colors?.[0]]?.front && (
                <img src={fileUrl(featured.sample_product.mockups[featured.sample_product.colors[0]].front)}
                  alt={featured.design.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              )}
            </Link>
            <div className="p-8 lg:p-16">
              <div className="inline-block bg-[color:var(--hb-royal)] text-white text-[10px] tracking-[0.35em] uppercase px-3 py-1 mb-4">
                {featured.reason === "best_seller" ? "Design of the Month" : "Featured Design"}
              </div>
              <h2 className="font-display text-5xl text-[color:var(--hb-navy)]">{featured.design.name}</h2>
              <p className="mt-4 text-neutral-700 leading-relaxed">{featured.design.description}</p>
              <div className="mt-6 flex flex-wrap gap-2">
                {featured.design.collections?.slice(0,4).map(c => (
                  <span key={c} className="text-[10px] tracking-widest uppercase border border-neutral-300 px-2 py-1 text-neutral-600">{c.replace(/-/g," ").replace(/\bofficial\b/i,"Official").replace(/\bcollection\b/i,"Collection")}</span>
                ))}
              </div>
              <Link to={`/product/${featured.sample_product.product_id}`} className="hb-btn-primary mt-8 inline-block" data-testid="featured-shop-btn">Shop This Design</Link>
            </div>
          </div>
        </section>
      )}

      {/* SENIORS COLLECTION — Featured */}
      <section className="relative overflow-hidden" data-testid="seniors-feature-section">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1D4ED8] via-[#0A1F44] to-[#0D2864]"/>
        <div className="absolute inset-0 opacity-30 mix-blend-overlay" style={{backgroundImage:"radial-gradient(circle at 20% 20%, #FFD700 0%, transparent 40%), radial-gradient(circle at 80% 60%, #1D4ED8 0%, transparent 45%)"}}/>
        <div className="absolute inset-0 hb-grain opacity-40"/>
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 py-24 grid lg:grid-cols-2 gap-12 items-center text-white">
          <div>
            <div className="inline-flex items-center gap-2 bg-[#FFD700] text-[#0A1F44] text-[10px] tracking-[0.35em] uppercase px-3 py-1.5 font-bold mb-6">
              <span>New</span><span className="opacity-60">·</span><span>Class of 2027</span>
            </div>
            <h2 className="font-display text-6xl sm:text-7xl leading-[0.9]">
              Seniors<br/>
              <span className="text-[#FFD700]">Collection</span>
            </h2>
            <p className="mt-6 text-lg text-white/85 max-w-lg font-light leading-relaxed">
              Celebrate the final chapter. Custom apparel and DTF transfers for graduating seniors — Class of 2027 shirts, school colors, graduation designs, senior athletes, and every unforgettable memory in between.
            </p>
            <ul className="mt-6 grid grid-cols-2 gap-x-6 gap-y-2 text-sm text-white/70 max-w-lg">
              <li className="flex items-center gap-2"><span className="text-[#FFD700]">✦</span> Class of 2027 designs</li>
              <li className="flex items-center gap-2"><span className="text-[#FFD700]">✦</span> Senior athlete gear</li>
              <li className="flex items-center gap-2"><span className="text-[#FFD700]">✦</span> Cap & gown designs</li>
              <li className="flex items-center gap-2"><span className="text-[#FFD700]">✦</span> School colors & mascots</li>
              <li className="flex items-center gap-2"><span className="text-[#FFD700]">✦</span> "Our Final Chapter"</li>
              <li className="flex items-center gap-2"><span className="text-[#FFD700]">✦</span> Personalized names & years</li>
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/collections/seniors-collection" className="hb-btn-primary bg-[#FFD700] text-[#0A1F44] border-[#FFD700] hover:bg-white hover:border-white" data-testid="seniors-shop-btn">
                Shop Seniors Collection
              </Link>
              <Link to="/custom-design" className="hb-btn-secondary text-white border-white hover:bg-white hover:text-[#0A1F44]" data-testid="seniors-custom-btn">
                Request Custom Senior Design
              </Link>
            </div>
          </div>
          <Link to="/collections/seniors-collection" className="relative aspect-[4/5] lg:aspect-square overflow-hidden group" data-testid="seniors-hero-tile">
            <div className="absolute inset-0 bg-gradient-to-br from-[#1D4ED8]/40 to-[#0A1F44]/80 z-10"/>
            <img src="https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=1200&auto=format&fit=crop" alt="Seniors Collection — Class of 2027" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"/>
            <div className="absolute inset-0 z-20 flex flex-col justify-end p-8">
              <div className="font-display text-4xl lg:text-5xl leading-tight tracking-tight">CLASS OF<br/><span className="text-[#FFD700]">2027</span></div>
              <div className="mt-3 text-xs tracking-[0.35em] uppercase opacity-80 border-t border-white/40 pt-3">Our Final Chapter · Next Chapter Awaits</div>
            </div>
          </Link>
        </div>
      </section>

      {/* STORY */}
      <section className="bg-[color:var(--hb-navy)] text-white py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="text-xs tracking-[0.35em] uppercase text-white/60 mb-4">Family Owned. New York Made.</div>
          <h3 className="font-serif-editorial text-4xl sm:text-5xl leading-tight">
            "We build every design with the people who wear it in mind — pride, purpose, and the streets we come from."
          </h3>
          <div className="mt-8 text-sm tracking-widest uppercase opacity-70">— The Hampton Blu Family</div>
          <Link to="/about" className="mt-8 inline-block hb-btn-secondary border-white text-white hover:bg-white hover:text-[color:var(--hb-navy)]" data-testid="story-about-btn">Our Story</Link>
        </div>
      </section>

      {/* DOODLES ROW */}
      {doodles.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 sm:px-8 py-20">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="text-xs tracking-[0.3em] uppercase text-neutral-500 mb-2">Woof · Pack Life</div>
              <h2 className="font-display text-5xl text-[color:var(--hb-navy)]">The Doodles Official Collection</h2>
            </div>
            <Link to="/collections/the-doodles-official-collection" className="text-sm uppercase tracking-widest inline-flex items-center gap-1 hover:text-[color:var(--hb-royal)]">View all <ArrowRight size={16}/></Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {doodles.slice(0,6).map(p => <ProductCard key={p.product_id} product={p} />)}
          </div>
        </section>
      )}
    </div>
  );
}
